import ResourceManager from "../../components/ResourceManager"
import { useOptions } from "../../options/hooks/use-options"
import { StringMap } from "../../types/common"
import { useI18n } from "../../hooks"
import { TaskViewModel } from "../api/task"
import { useTaskMutations } from "../hooks/use-task-mutations"

const TaskSuppliersWidget = ({
  task,
  boardId,
  columnPosition,
}: {
  task: TaskViewModel
  boardId?: string
  columnPosition?: number
}) => {
  const translations = useTranslations(defaultTranslations)
  const { supplierOptions } = useOptions(task.maintainerId)
  const { addSuppliers, removeSuppliers } = useTaskMutations()
  const handleAddSuppliers = (ids: string[]) => {
    const suppliers = supplierOptions
      .filter((supplier) => ids.includes(supplier.id))
      .map((supplier) => ({ name: supplier.name, id: supplier.id }))
    return addSuppliers(task.id, suppliers, boardId, columnPosition)
  }

  const handleRemoveSuppliers = (id: string) => {
    const suppliers = task.suppliers
      .filter((supplier) => supplier.id === id)
      .map((supplier) => ({ name: supplier.name, id: supplier.id }))
    return removeSuppliers(task.id, { suppliers, boardId })
  }

  const sortedSupplierOptions = supplierOptions
    .slice()
    .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))
  const sortedTaskSuppliers = task.suppliers
    .slice()
    .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))

  return (
    <ResourceManager
      title={translations.title}
      resourceOptions={sortedSupplierOptions}
      resources={sortedTaskSuppliers}
      canAddResource={task.canUpdateSuppliers}
      canRemoveResource={task.canUpdateSuppliers}
      onAddResources={handleAddSuppliers}
      onRemoveResource={handleRemoveSuppliers}
      emptyOptionsMessage={translations.emptyOptionsMessage}
      emptyResourcesMessage={translations.emptyResourcesMessage}
      isMultipleSelection
    />
  )
}

const useTranslations = (defaults: Translations = defaultTranslations): Translations => {
  const { translations: t } = useI18n("task")
  const translations = (t?.suppliersWidget || {}) as StringMap

  const {
    title = defaults.title,
    emptyOptionsMessage = defaults.emptyOptionsMessage,
    emptyResourcesMessage = defaults.emptyResourcesMessage,
  } = translations

  return {
    title,
    emptyOptionsMessage,
    emptyResourcesMessage,
  }
}

const defaultTranslations = {
  title: "Suppliers",
  emptyOptionsMessage: "No suppliers in this organisation",
  emptyResourcesMessage: "No suppliers in this task",
}

type Translations = typeof defaultTranslations

export default TaskSuppliersWidget
